import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { apiError } from "@/lib/platform";
import { useAuth } from "@/context/AuthContext";
import { useDirectory } from "@/lib/rights";

const ALL = "__all";
const BLANK = { request_type: "ACCESS", subject_type: "CUSTOMER", subject_name: "", linked_person_key: "", subject_email: "",
  subject_phone: "", received_date: "", target_date: "", owner: "", channel: "EMAIL", description: "", ropa_assessment_id: ALL,
  nominee: { name: "", relationship: "", email: "", phone: "" } };

const Field = ({ label, children, hint }) => (
  <div className="space-y-1.5">
    <Label className="text-xs">{label}</Label>
    {children}
    {hint && <div className="text-[11px] text-slate-500">{hint}</div>}
  </div>
);

const Pick = ({ value, onChange, options, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-9 text-sm" data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent>{options.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

export const NewRequestDialog = ({ open, onOpenChange, meta }) => {
  const auth = useAuth();
  const nav = useNavigate();
  const users = useDirectory();
  const [f, setF] = useState(BLANK);
  const [assessments, setAssessments] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setF({ ...BLANK, owner: auth.user?.username || "" });
    api.get("/platform/assessments").then(({ data }) => setAssessments(data.assessments)).catch(() => {});
  }, [open, auth.user?.username]);

  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  const setN = (k) => (e) => setF((x) => ({ ...x, nominee: { ...x.nominee, [k]: e.target.value } }));
  const sla = meta?.settings?.sla_days || 30;
  const methods = meta?.settings?.verification?.[f.subject_type]?.methods || [];

  const submit = async () => {
    setSaving(true);
    try {
      const body = { ...f, ropa_assessment_id: f.ropa_assessment_id === ALL ? null : f.ropa_assessment_id,
        received_date: f.received_date || null, target_date: f.target_date || null,
        nominee: f.request_type === "NOMINEE" ? f.nominee : null };
      const { data } = await api.post("/rights", body);
      toast.success(`${data.request_id} logged · ${data.systems.length} system(s) mapped from RoPA`);
      onOpenChange(false);
      nav(`/rights/${data.id}`);
    } catch (e) {
      toast.error(apiError(e, "Could not log request"));
    } finally {
      setSaving(false);
    }
  };

  if (!meta) return null;
  const valid = f.subject_name.trim() && f.linked_person_key.trim() && (f.request_type !== "NOMINEE" || f.nominee.name.trim());
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-request-dialog">
        <DialogHeader>
          <DialogTitle>Log a rights request</DialogTitle>
          <DialogDescription>The person key is used to map where this person's data sits, based on the RoPA.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Request type">
            <Pick value={f.request_type} onChange={set("request_type")} options={Object.entries(meta.request_types)} testId="new-request-type" />
          </Field>
          <Field label="Subject type" hint={`Verify with: ${methods.join(" · ")}`}>
            <Pick value={f.subject_type} onChange={set("subject_type")} options={Object.entries(meta.subject_types)} testId="new-request-subject-type" />
          </Field>
          <Field label="Subject name">
            <Input value={f.subject_name} onChange={set("subject_name")} data-testid="new-request-subject-name" />
          </Field>
          <Field label="Linked person key" hint="e.g. Customer ID, Employee ID or Vendor reference">
            <Input value={f.linked_person_key} onChange={set("linked_person_key")} className="font-mono" data-testid="new-request-person-key" />
          </Field>
          <Field label="Email">
            <Input type="email" value={f.subject_email} onChange={set("subject_email")} data-testid="new-request-email" />
          </Field>
          <Field label="Mobile">
            <Input value={f.subject_phone} onChange={set("subject_phone")} data-testid="new-request-phone" />
          </Field>
          <Field label="Received date" hint="Defaults to today">
            <Input type="date" value={f.received_date} onChange={set("received_date")} data-testid="new-request-received" />
          </Field>
          <Field label="Target date" hint={`Defaults to received date + ${sla} days`}>
            <Input type="date" value={f.target_date} onChange={set("target_date")} data-testid="new-request-target" />
          </Field>
          <Field label="Owner">
            <Pick value={f.owner} onChange={set("owner")} testId="new-request-owner"
                  options={users.map((u) => [u.username, u.full_name || u.username])} />
          </Field>
          <Field label="Received via">
            <Pick value={f.channel} onChange={set("channel")} options={Object.entries(meta.channels)} testId="new-request-channel" />
          </Field>
          <div className="sm:col-span-2">
            <Field label="RoPA register to search" hint={`${meta.subject_types[f.subject_type]} maps to RoPA category "${meta.ropa_category_map[f.subject_type]}"`}>
              <Pick value={f.ropa_assessment_id} onChange={set("ropa_assessment_id")} testId="new-request-ropa-scope"
                    options={[[ALL, "All RoPA registers in this organization"],
                      ...assessments.map((a) => [a.assessment_id, `${a.org_name || a.assessment_id.slice(0, 8)} · L${a.maturity_level ?? "?"}`])]} />
            </Field>
          </div>
          {f.request_type === "NOMINEE" && (
            <div className="sm:col-span-2 grid grid-cols-2 gap-3 border border-amber-200 bg-amber-50/40 rounded-lg p-3" data-testid="new-request-nominee">
              <Input placeholder="Nominee name" value={f.nominee.name} onChange={setN("name")} data-testid="nominee-name" />
              <Input placeholder="Relationship" value={f.nominee.relationship} onChange={setN("relationship")} data-testid="nominee-relationship" />
              <Input placeholder="Nominee email" value={f.nominee.email} onChange={setN("email")} data-testid="nominee-email" />
              <Input placeholder="Nominee mobile" value={f.nominee.phone} onChange={setN("phone")} data-testid="nominee-phone" />
            </div>
          )}
          <div className="sm:col-span-2">
            <Field label="Request details">
              <Textarea rows={3} value={f.description} onChange={set("description")} data-testid="new-request-description" />
            </Field>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !valid} className="bg-[#008080] hover:bg-[#008080]/90" data-testid="new-request-submit">
            {saving ? "Logging…" : "Log request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
